import { z } from 'zod'
import i18n from '@/i18n'

// Validation messages are resolved via i18n (French default, English fallback).
// i18n is initialized in main.tsx before this module loads, so the lookups
// below resolve at module-evaluation time. The app language is fixed, so the
// resolved strings never need to change after load.
const v = (key: string) => i18n.t(`auth:validation.${key}`)

// --- Login (2.1.1) ---
// Email: required, max 100, valid email format.
// Password: required, min 8, max 64.
export const loginSchema = z.object({
  email: z
    .string()
    .min(1, v('emailRequired'))
    .max(100, v('emailRequired'))
    .email(v('emailInvalid')),
  password: z
    .string()
    .min(1, v('passwordRequired'))
    .min(8, v('passwordRequired'))
    .max(64, v('passwordRequired')),
})
export type LoginFormData = z.infer<typeof loginSchema>

// --- Forgot password (2.1.2 screen 1) ---
export const forgotPasswordSchema = z.object({
  email: z
    .string()
    .min(1, v('emailRequired'))
    .max(100, v('emailRequired'))
    .email(v('emailInvalid')),
})
export type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>

// --- Password strength rules (2.1.2-006): min 8, >=1 upper, >=1 lower, >=1 number, >=1 special ---
export const PASSWORD_MIN = 8
export const PASSWORD_MAX = 64

export const passwordStrengthRules = [
  { key: 'length', label: i18n.t('auth:passwordRules.length'), test: (val: string) => val.length >= PASSWORD_MIN },
  { key: 'upper', label: i18n.t('auth:passwordRules.upper'), test: (val: string) => /[A-Z]/.test(val) },
  { key: 'lower', label: i18n.t('auth:passwordRules.lower'), test: (val: string) => /[a-z]/.test(val) },
  { key: 'number', label: i18n.t('auth:passwordRules.number'), test: (val: string) => /[0-9]/.test(val) },
  { key: 'special', label: i18n.t('auth:passwordRules.special'), test: (val: string) => /[^A-Za-z0-9]/.test(val) },
] as const

export function passwordMeetsStrength(value: string): boolean {
  return passwordStrengthRules.every(r => r.test(value))
}

const strongPassword = z
  .string()
  .min(PASSWORD_MIN)
  .max(PASSWORD_MAX)
  .refine(passwordMeetsStrength, {
    message: v('passwordStrength'),
  })

// --- Reset / Set password (2.1.2 screen 2) ---
export const resetPasswordSchema = z
  .object({
    password: strongPassword,
    password_confirmation: z.string().min(1, v('confirmRequired')),
  })
  .refine(data => data.password === data.password_confirmation, {
    message: v('passwordsNoMatch'),
    path: ['password_confirmation'],
  })
export type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>
